/**
 * InspectionRangementSection — Inspections de rangement par unité.
 *
 * Affichée dans la page Unités, sur chaque carte. Remplace progressivement
 * l'onglet « Rangement » de la page Inspection (qui demandait de choisir
 * un chef d'équipe avant de faire l'inspection) — beaucoup plus logique
 * de partir de l'unité directement.
 *
 * Permissions :
 *  - canEdit (admin/CP/adjoint) : peut créer / modifier / supprimer
 *  - chef d'équipe : lecture seule (ses inspections uniquement, filtrées
 *    par chef_equipe_id côté frontend)
 */
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, XCircle, Plus, Trash2, Edit3, Camera, ChevronDown, ChevronUp, ClipboardCheck, User, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function InspectionRangementSection({ unite, canEdit, isChefEquipe }) {
  const { user } = useAuth();
  const [inspections, setInspections] = useState([]);
  const [criteres, setCriteres] = useState([]);
  const [frequenceJours, setFrequenceJours] = useState(14);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingInsp, setEditingInsp] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const inspParams = { unite_rangement_id: unite.id };
      const [insRes, critRes, cfgRes] = await Promise.all([
        axios.get(`${API_URL}/api/inspections/rangement`, { params: inspParams }),
        axios.get(`${API_URL}/api/inspections/criteres/rangement_unites`),
        axios.get(`${API_URL}/api/inspections/rangement/config`).catch(() => ({ data: { frequence_jours: 14 } })),
      ]);
      let list = insRes.data?.inspections || [];
      if (isChefEquipe && (user?.id || user?.employee_id)) {
        list = list.filter(i =>
          i.chef_equipe_id === user.id || i.chef_equipe_id === user.employee_id
        );
      }
      setInspections(list);
      setCriteres(critRes.data?.criteres || []);
      setFrequenceJours(cfgRes.data?.frequence_jours || 14);
    } catch (e) {
      console.error('Erreur chargement inspections rangement:', e);
      toast.error('Erreur chargement inspections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [unite.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette inspection ?')) return;
    try {
      await axios.delete(`${API_URL}/api/inspections/rangement/${id}`);
      toast.success('Inspection supprimée');
      reload();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  if (showForm || editingInsp) {
    return (
      <FormInspection
        unite={unite}
        criteres={criteres}
        inspection={editingInsp}
        onSaved={() => { setShowForm(false); setEditingInsp(null); reload(); }}
        onCancel={() => { setShowForm(false); setEditingInsp(null); }}
      />
    );
  }

  // ✅ Calcul du retard pour la bannière de rappel
  const derniere = inspections[0]; // déjà triées date desc côté backend
  let joursDepuisDerniere = null;
  if (derniere?.date_inspection) {
    const d = new Date(derniere.date_inspection);
    if (!isNaN(d.getTime())) {
      joursDepuisDerniere = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    }
  }
  const enRetard = joursDepuisDerniere === null || joursDepuisDerniere > frequenceJours;
  const bannerLabel = derniere
    ? `Dernière inspection il y a ${joursDepuisDerniere} jour${joursDepuisDerniere > 1 ? 's' : ''} — fréquence recommandée : ${frequenceJours} jours.`
    : `Cette unité n'a jamais été inspectée. Fréquence recommandée : ${frequenceJours} jours.`;

  return (
    <div className="space-y-3" data-testid="inspection-rangement-section">
      {/* ✅ Bannière de rappel intelligent */}
      {!loading && enRetard && (
        <div
          className="flex items-start gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50"
          data-testid="banner-inspection-retard"
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 text-xs text-amber-900">
            <p className="font-semibold">Inspection à prévoir</p>
            <p className="text-amber-800">{bannerLabel}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-500">
          {isChefEquipe
            ? 'Vos inspections de rangement pour cette unité (lecture seule).'
            : 'Inspections de rangement effectuées sur cette unité.'}
        </p>
        {canEdit && (
          <Button size="sm" onClick={() => setShowForm(true)} data-testid="btn-nouvelle-inspection-rangement">
            <Plus className="w-4 h-4 mr-1.5" /> Nouvelle inspection
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-center py-6 text-sm text-slate-400">Chargement...</p>
      ) : inspections.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-xl">
          <ClipboardCheck className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-500">Aucune inspection de rangement pour cette unité.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {inspections.map(insp => (
            <Card key={insp.id} className={`border-l-4 ${insp.reussie ? 'border-l-emerald-500' : 'border-l-red-500'}`}>
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    {insp.reussie
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      : <XCircle className="w-4 h-4 text-red-600 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {new Date(insp.date_inspection + 'T12:00:00').toLocaleDateString('fr-CA', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                      <p className="text-[11px] text-slate-500 flex items-center gap-1 flex-wrap">
                        <User className="w-3 h-3" />{insp.inspecteur_nom || 'N/A'}
                        {insp.chef_equipe_nom && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span>Chef : {insp.chef_equipe_nom}</span>
                          </>
                        )}
                        <span className="text-slate-300">·</span>
                        {insp.nb_reussi}/{insp.nb_total}
                        {insp.nb_echoue > 0 && <span className="text-red-500">· {insp.nb_echoue} échec(s)</span>}
                      </p>
                    </div>
                    <Badge className={insp.reussie ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
                      {insp.reussie ? 'Conforme' : 'Non conforme'}
                    </Badge>
                  </div>
                  <div className="flex gap-0.5 shrink-0 ml-2">
                    <Button size="sm" variant="ghost" onClick={() => setExpandedId(expandedId === insp.id ? null : insp.id)}>
                      {expandedId === insp.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                    {canEdit && (
                      <>
                        <Button size="sm" variant="ghost" className="text-blue-500" onClick={() => setEditingInsp(insp)}>
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(insp.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {expandedId === insp.id && (
                  <div className="mt-3 pt-3 border-t space-y-2">
                    {insp.notes && (
                      <p className="text-xs text-slate-600 bg-slate-50 p-2 rounded">{insp.notes}</p>
                    )}
                    {(insp.photos || []).length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {insp.photos.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noreferrer" className="block">
                            <img src={url} alt="" className="w-16 h-16 object-cover rounded border" />
                          </a>
                        ))}
                      </div>
                    )}
                    <ul className="text-xs space-y-1">
                      {(criteres || []).map(c => {
                        const ok = insp.reponses?.[c.id];
                        if (ok === undefined) return null;
                        return (
                          <li key={c.id} className="flex items-start gap-1.5">
                            {ok
                              ? <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                              : <XCircle className="w-3 h-3 text-red-500 mt-0.5 shrink-0" />}
                            <span className={ok ? 'text-slate-700' : 'text-red-700'}>{c.label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Formulaire d'inspection (création / édition) ───────────────────────────
function FormInspection({ unite, criteres, inspection, onSaved, onCancel }) {
  const { user } = useAuth();
  const [date, setDate] = useState(
    inspection?.date_inspection?.split('T')[0] || new Date().toISOString().split('T')[0]
  );
  const [reponses, setReponses] = useState(inspection?.reponses || {});
  const [notes, setNotes] = useState(inspection?.notes || '');
  const [photos, setPhotos] = useState(inspection?.photos || []);
  const [chefEquipeId, setChefEquipeId] = useState(inspection?.chef_equipe_id || '');
  const [chefEquipeNom, setChefEquipeNom] = useState(inspection?.chef_equipe_nom || '');
  const [teamLeads, setTeamLeads] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/team-leads`).then(r => setTeamLeads(r.data?.team_leads || [])).catch(() => {});
    // Si l'unité est assignée à un chef, pré-remplir
    if (!inspection && unite?.assigne_a?.id) {
      setChefEquipeId(unite.assigne_a.id);
      setChefEquipeNom(unite.assigne_a.nom);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const nbTotal = criteres.length;
  const nbReussi = criteres.filter(c => reponses[c.id] === true).length;
  const nbEchoue = criteres.filter(c => reponses[c.id] === false).length;
  const reussie = nbEchoue === 0 && nbTotal > 0;

  const toggleReponse = (id) => {
    setReponses(prev => {
      if (prev[id] === undefined) return { ...prev, [id]: true };
      if (prev[id] === true) return { ...prev, [id]: false };
      const copy = { ...prev }; delete copy[id]; return copy;
    });
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await axios.post(`${API_URL}/api/upload`, fd);
        if (res.data?.url) setPhotos(prev => [...prev, res.data.url]);
      } catch { toast.error('Erreur upload photo'); }
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSave = async () => {
    if (!date) { toast.error('Date requise'); return; }
    if (!chefEquipeId) { toast.error('Sélectionnez un chef d\'équipe'); return; }
    setSaving(true);
    try {
      const payload = {
        date_inspection: date,
        inspecteur_id: user?.id || '',
        inspecteur_nom: user ? `${user.prenom || ''} ${user.nom || ''}`.trim() : '',
        chef_equipe_id: chefEquipeId,
        chef_equipe_nom: chefEquipeNom,
        unite_rangement_id: unite.id,
        unite_rangement_nom: unite.nom || '',
        notes,
        photos,
        reponses,
      };
      const url = inspection
        ? `${API_URL}/api/inspections/rangement/${inspection.id}`
        : `${API_URL}/api/inspections/rangement`;
      if (inspection) await axios.put(url, payload);
      else await axios.post(url, payload);
      toast.success(inspection ? 'Inspection mise à jour' : 'Inspection créée');
      onSaved();
    } catch {
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-emerald-600" />
          {inspection ? 'Modifier l\'inspection' : `Nouvelle inspection — ${unite.nom}`}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Date <span className="text-red-500">*</span></Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div>
            <Label>Chef d'équipe <span className="text-red-500">*</span></Label>
            <select
              className="w-full border rounded-md px-3 py-2 text-sm bg-white"
              value={chefEquipeId}
              onChange={e => {
                const sel = teamLeads.find(t => t.id === e.target.value);
                setChefEquipeId(e.target.value);
                setChefEquipeNom(sel?.name || '');
              }}
              data-testid="select-chef-equipe-inspection"
            >
              <option value="">— Sélectionner —</option>
              {teamLeads.map(tl => <option key={tl.id} value={tl.id}>{tl.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Inspecté par</Label>
            <Input value={user ? `${user.prenom || ''} ${user.nom || ''}`.trim() : ''} disabled className="bg-slate-50" />
          </div>
        </div>

        {Object.keys(reponses).length > 0 && (
          <div className={`flex items-center gap-2 p-2 rounded-lg border text-sm ${reussie ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
            {reussie
              ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              : <XCircle className="w-4 h-4 text-red-600 shrink-0" />}
            <span className="font-medium">
              {reussie ? 'Inspection réussie' : `${nbEchoue} critère(s) échoué(s)`}
            </span>
            <span className="ml-auto text-xs text-slate-500">{nbReussi}/{nbTotal}</span>
          </div>
        )}

        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-2 block">Critères</Label>
          <div className="space-y-1.5">
            {(criteres || []).map(c => {
              const v = reponses[c.id];
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleReponse(c.id)}
                  className={`w-full text-left flex items-start gap-2 p-2 rounded-md border text-sm transition ${
                    v === true ? 'bg-emerald-50 border-emerald-300' :
                    v === false ? 'bg-red-50 border-red-300' :
                    'bg-white border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {v === true ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    : v === false ? <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                    : <span className="w-4 h-4 rounded-full border-2 border-slate-300 mt-0.5 shrink-0" />}
                  <span>{c.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label>Notes</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Observations..." />
        </div>

        <div>
          <Label>Photos</Label>
          <div className="flex items-center gap-2 flex-wrap">
            {photos.map((url, i) => (
              <div key={i} className="relative group">
                <img src={url} alt="" className="w-16 h-16 object-cover rounded border" />
                <button onClick={() => setPhotos(p => p.filter((_, idx) => idx !== i))}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100">
                  ×
                </button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
              <Camera className="w-4 h-4 mr-1" /> Ajouter
            </Button>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="btn-save-inspection-rangement">
            {saving ? 'Enregistrement...' : inspection ? 'Mettre à jour' : 'Enregistrer'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
